import {
  CartIcon,
  SearchIcon,
  UserIcon,
  MenuIconResponsive,
  XIcon,
  CartMobileIcon,
} from "@components/atoms/Icons/index";
import { useResponsiveNavbar } from "@models/interactions/use-responsive-navbar";
import { ResetPasswordPopup } from "@components/molecules/popup/index.client";
import { HeaderCart } from "@components/molecules/header-cart/index.client";
import NavigationResponsive from "@components/molecules/navbar-responsive";
import { Profile } from "@components/molecules/profile/index.client";
import { CartBadge } from "@components/molecules/cart/badge.client";
import { usePopup } from "@models/interactions/use-popup/index";
import kanvasAuth from "@models/interactions/kanvas-auth/index";
import { useAuth } from "@models/interactions/use-auth-modal";
import { useDrawer } from "@models/interactions/use-drawer";
import Navigation from "@components/molecules/navbar/index";
import { CartDetails } from "@components/organisms/Cart";
import { Drawer } from "@components/organisms/Drawer";
import {
  clubStateAtom,
  customerAtom,
  expStateAtom,
  levelsAtom,
  isOpenMailPreferencesPopup,
  userAtom,
} from "@models/state/customer";
import { Link, useNavigate } from "@shopify/hydrogen";
import { HeaderLayout } from "../header-layout";
import { translate } from "@locales";
import { useAtom, useSetAtom } from "jotai";
import {
  isOpenPasswordRestoredPopupAtom,
  openAccountPopupAtom,
  openPopupAtom,
  openNewsletterPopup,
} from "@models/state/cart/index";

import { CreateAccountPopup } from "@components/molecules/create-account-popup/index.client";
import { PasswordRestoredPopup } from "@components/molecules/password-restored-popup/index";
import { TranslationMenu } from "@components/molecules/translation-menu/index.client";
import { useCart } from "@shopify/hydrogen";
import { NewsletterPopup } from "@components/molecules/newsletter-popup";
import { MailPreferencePopup } from "@components/molecules/mail-preferences-popup/index.client";

export function useMainHeader() {
  const navigate = useNavigate();
  const [user, setUser] = useAtom(customerAtom);
  const setCustomer = useSetAtom(userAtom);
  const setLevels = useSetAtom(levelsAtom);
  const setExp = useSetAtom(expStateAtom);
  const setClubState = useSetAtom(clubStateAtom);

  function logout() {
    fetch("/api/logout", {
      method: "POST",
    }).then(() => {
      setUser(null);
      setCustomer(null);
      setExp("");
      setLevels([]);
      setClubState("");
      kanvasAuth.logout();
      navigate("/");
    });
  }
  return {
    models: {
      user,
    },
    operations: {
      logout,
    },
  };
}

interface MainHeaderProps {
  menu: unknown;
  openSearch: () => void;
}

export function MainHeader({ menu, openSearch }: MainHeaderProps) {
  const { models, operations } = useDrawer();
  const { models: headerModels, operations: headersOperations } =
    useMainHeader();
  const { models: popupModels, operations: operationsPopup } =
    usePopup(openPopupAtom);

  const { models: accountModel, operations: AccountOperations } =
    usePopup(openAccountPopupAtom);

  const { models: newsletterModel, operations: newsletterOperations } =
    usePopup(openNewsletterPopup);

  const mailPreferencePopup = usePopup(isOpenMailPreferencesPopup);
  const passwordRestoredPopup = usePopup(isOpenPasswordRestoredPopupAtom);

  const auth = useAuth();

  const { totalQuantity } = useCart();

  return (
    <>
      <Drawer
        open={models.isOpen}
        onClose={operations.closeDrawer}
        header={<HeaderCart onclose={operations.closeDrawer} />}
        direction="right"
      >
        <div className="grid justify-center min-h-[80%]">
          <Drawer.Title>
            <p className="sr-only">{translate("base.drawer")}</p>
          </Drawer.Title>
          <CartDetails onClose={operations.closeDrawer} />
        </div>
      </Drawer>
      <ResetPasswordPopup
        isOpen={popupModels.isOpen}
        onClose={operationsPopup.closePopup}
      />
      <CreateAccountPopup
        isOpen={accountModel.isOpen}
        onClose={AccountOperations.closePopup}
      />
      <PasswordRestoredPopup
        isOpen={passwordRestoredPopup.models.isOpen}
        onClose={passwordRestoredPopup.operations.closePopup}
      />
      <NewsletterPopup
        isOpen={newsletterModel.isOpen}
        onClose={newsletterOperations.closePopup}
      />
      <MailPreferencePopup
        isOpen={mailPreferencePopup.models.isOpen}
        onClose={mailPreferencePopup.operations.closePopup}
      />
      <HeaderLayout>
        <div className="flex justify-between lg:justify-start w-full lg:w-0 lg:flex-1">
          {/* TODO: change this to be a generic logo component */}
          <button
            className="flex items-center lg:hidden"
            onClick={useResponsiveNavbar().operations.openDrawer}
          >
            {useResponsiveNavbar().models.open ? (
              <XIcon width="12px" height="12px" />
            ) : (
              <MenuIconResponsive width="15px" height="12px" />
            )}
          </button>
          <Link to="/" className="flex items-center">
            <img
              className="object-contain w-16 h-9 lg:w-auto lg:pl-8 lg:h-14 justify-start"
              src="https://cdn.shopify.com/s/files/1/0044/3708/2200/t/10/assets/too-logo.svg?v=90775149919688690841647538833"
              alt="Copic Logo"
            />
          </Link>
          <div className="flex flex-row space-x-4 items-center object-contain lg:hidden">
            <button onClick={openSearch}>
              <SearchIcon width="20px" height="20px" />
            </button>
            {!headerModels.user ? (
              <Link to="/sign-in">
                <UserIcon width="20px" height="20px" />
              </Link>
            ) : (
              <Profile
                profile={headerModels.user}
                logout={headersOperations.logout}
              />
            )}
            <Link to="/cart">
              {totalQuantity !== 0 ? (
                <CartMobileIcon
                  number={totalQuantity}
                  width="20px"
                  height="20px"
                />
              ) : (
                <CartIcon width="20px" height="20px" />
              )}
            </Link>
          </div>
          <Drawer
            open={useResponsiveNavbar().models.open}
            onClose={useResponsiveNavbar().operations.openDrawer}
          >
            <NavigationResponsive menu={menu} />
          </Drawer>
        </div>
        <Navigation menu={menu} />
        <div className="items-center justify-end hidden space-x-5 lg:flex lg:flex-1 lg:w-0 font-chivo-regular">
          <button onClick={openSearch}>
            <SearchIcon width="20px" height="20px" />
          </button>
          {!headerModels.user ? (
            <>
              <UserIcon width="20px" height="20px" />
              <p style={{ fontSize: "10px" }}>
                <Link to="/sign-in">{translate("base.signin")}</Link>/{" "}
                <Link to="/sign-up">{translate("base.register")}</Link>
              </p>
            </>
          ) : (
            <Profile
              profile={headerModels.user}
              logout={headersOperations.logout}
            />
          )}

          {/* TODO: cart drawer component */}

          <Link to="/cart" className="flex cursor-pointer">
            <CartIcon width="20px" height="20px" />

            <CartBadge className="pl-[5px]" />
          </Link>

          {/* TODO: Language isocode select this is just for markup */}
          <TranslationMenu
            classname="flex flex-row w-full items-center justify-center gap-x-1.5 bg-white px-3 py-2 text-sm text-gray-900"
            height="12px"
            width="20px"
            pclassname="pl-1 text-sm font-chivo-light"
          />
        </div>
      </HeaderLayout>
    </>
  );
}
